<template>
<div>
<div v-if="!subscriptionDetails">
    <email-address-field
    id="email"
    class="email"
    :mandatory="true"
    :is-valid="!errorHandler.has('email')"
    v-model="email"/>
    <span v-if="errorHandler.has('message')" class="error-message">{{ errorHandler.get('message') }}</span>
    <span v-if="errorHandler.has('isCancelled')" class="error-message">{{errorHandler.get('isCancelled')}}</span>

    <div class="text-center">
        <submit-button
            @click="submitStep"
            :processing="isFormProcessing"
            color="green"
        >
            {{ $t("ACCOUNT_FORM_NEXT_STEP") }}
        </submit-button>
    </div>
</div>
<div v-if="subscriptionDetails" class="subscription-details">
        <h3>Subscription Details</h3>
        <p>
            <strong>Your Subscription:</strong>
            <select v-model="editedProductFamily">
                <option v-for="option in productFamilyOptions" :key="option.id" :value="option.id">
                    {{ option.display_name }} - {{currencySymbol}} {{ option.price }}
                </option>
            </select>
        </p>
        <div class="checkbox-container" v-if="selectedProductFamilyIsStandard">
            <img src="../../../assets/images/decoration/flowers.svg"/>
            <label>
                {{$t('Dislike Lily ?')}}
                <input class="lily-input" type="checkbox" v-model="dislikeLily">
            </label>
        </div>
        <div class="checkbox-container">
            <img src="../../../assets/images/decoration/email-icon.png"/>
            <label>
                {{$t('Likes Weekly Email ?')}}
                <input class="likes-email" type="checkbox" v-model="likesEmail">
            </label>
        </div>
        <p>
            <strong>Your Frequency:</strong>
            <select v-model="editedPlanFrequency">
                <option v-for="option in planFrequencyOptions" :key="option.id" :value="option.slug">
                    {{ option.name }}
                </option>
            </select>
        </p>

        <p><strong>Delivery Amount:</strong> <span>{{ currencySymbol }} {{ subscriptionDetails.default_delivery_amount }}</span></p>
        <div class="button-container">
            <button @click="toggleDeliveryFields" class="styled-button">{{  showDelivery ? 'Edit Delivery' : 'Back' }}</button>
        </div>
        <div v-if="showDelivery">
        <p><strong>Delivery Address:</strong> <span>{{ subscriptionDetails.delivery_address }}</span></p>
        <p><strong>Delivery Day:</strong> <span>{{ subscriptionDetails.delivery_day }}</span></p>
        </div>
        <div v-if="!showDelivery">
        <!-- Postcode Lookup Field -->
        <postcode-lookup-field
            class="contentsquare_sensitive_data"
            ref="postcode"
            :mandatory="true"
            address-type="shipping"
            v-model="address.postcode"
            @clear-address="clearAddress"
            @address-selected="updateSelectedAddress"
            :disabled="!showDelivery"/>

            <span v-if="errorHandler.has('postcode')" class="error-message">{{ errorHandler.get('postcode') }}</span>

        <!-- Address Line 1 -->
        <input-field
            class="contentsquare_sensitive_data"
            id="address_line_1"
            :mandatory="true"
            autocomplete="shipping address-line1"
            v-model="address.addressLine1"
            @change="addressChanged"
            :is-valid="!errorHandler.has('address_line_1')"
            :message="errorHandler.get('address_line_1')"
            :label="$t('DELIVERY_ADDRESS_FIRST_LINE')"/>
            <span v-if="errorHandler.has('address1')" class="error-message">{{ errorHandler.get('address1') }}</span>

        <!-- Address Line 2 -->
        <input-field
            class="contentsquare_sensitive_data"
            autocomplete="shipping address-line2"
            v-model="address.addressLine2"
            @change="addressChanged"
            :label="$t('DELIVERY_ADDRESS_SECOND_LINE')"/>

        <!-- City -->
        <input-field
            class="contentsquare_sensitive_data"
            id="city"
            :mandatory="true"
            autocomplete="shipping address-level2"
            v-model="address.city"
            @change="addressChanged"
            :is-valid="!errorHandler.has('city')"
            :message="errorHandler.get('city')"
            :label="$t('DELIVERY_ADDRESS_TOWN_OR_CITY')"/>
            <span v-if="errorHandler.has('city')" class="error-message">{{ errorHandler.get('city') }}</span>

        <!-- Delivery Waiting List Modal -->
        <delivery-waiting-list-modal
            :show-modal="showUndeliverablePostcodeModal"
            @closed="toggleUndeliverablePostcodeModal"
            :postcode="address.postcode"/>

            <div v-if="allowFirstDateSelection">
                <delivery-day-field
                    v-model="selectedDeliveryDate"
                    :postcode="address.postcode"/>

                <!-- Small gap -->
                <div class="clear--sm"/>
            </div>
    </div>
        <restart-account-coupon-field
                class="discount-text"
                :is-valid="!errorHandler.has('coupon_code')"
                :message="errorHandler.get('coupon_code')"
                v-model="couponCode"/>
        <div class="text-center">
            <submit-button
                @click="submitSubscription"
                :processing="isSubFormProcessing"
                :disabled="buttonDisabled"
                color="green"
            >
                Restart Now
            </submit-button>
        </div>
</div>
</div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import EmailAddressField from '@/components/forms/fields/EmailAddressField';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import RestartAccountCouponField from '@/components/forms/fields/RestartAccountCouponField';
import CouponService from '@/services/coupons/CouponService';
import { GIFT_COUPON_TYPE } from '@/constants/Gifting.js';
import PostcodeLookupField from '../../../components/forms/fields/PostcodeLookupField.vue';
import InputField from '@/components/forms/fields/InputField';
import DeliveryWaitingListModal from '@/components/modals/DeliveryWaitingListModal';
import DeliveryDayField from '@/components/forms/fields/DeliveryDayField';
import { isPostcodeFormatValid } from '@/helpers/PostcodeHelpers';
import PostcodeService from '@/services/logistics/PostcodeService';

export default {
    name: 'RestartAccount',
    data: () => {
        return {
            isFormProcessing: false,
            errorHandler: new ErrorResponseHandler(),
            subscriptionDetails: null,
            editedProductFamily: '',
            editedPlanFrequency: '',
            productFamilyOptions: [],
            planFrequencyOptions: [],
            buttonDisabled: false,
            subscriptionId: '',
            isSubFormProcessing: false,
            dislikeLily: false,
            likesEmail: false,
            address: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
            },
            showUndeliverablePostcodeModal: false,
            showDelivery: true,
            allowFirstDateSelection: true,
            selectedDeliveryDate: null,
        };
    },
    components: {
        EmailAddressField,
        SubmitButton,
        RestartAccountCouponField,
        PostcodeLookupField,
        InputField,
        DeliveryWaitingListModal,
        DeliveryDayField,
    },
    computed: {
        ...mapGetters({
            getProductTypes: 'subscription/getProductTypes',
            currencySymbol: 'localisation/getCurrency',
            sourceId: 'canvasser/getSourceId' || 'canvasser/getParentSourceId',
            canvasserId: 'account/getUserId',
            regionId: 'canvasser/getRegionId',
        }),
        email: {
            get() {
                return this.$store.getters['canvasser/getRestartEmail'];
            },
            set(value) {
                this.$store.commit('canvasser/setRestartEmail', value);
            },
        },
        couponCode: {
            get() {
                return this.$store.getters['register/getCouponCode'];
            },
            set(code) {
                this.$store.commit('register/setCouponCode', code);
            },
        },
        selectedProductFamilyIsStandard() {
            const selectedFamily = this.productFamilyOptions.find(
                option => option.id === this.editedProductFamily,
            );
            return selectedFamily && selectedFamily.name === 'standard';
        },
    },
    watch: {
        subscriptionDetails(newDetails) {
            if (newDetails) {
                this.dislikeLily = newDetails.user_dislike_lily;
                this.likesEmail = newDetails.user_likes_email;
            }
        },
        address: {
            deep: true,
            handler(address) {
                this.$emit('input', address);
                this.addressChanged();
            },
        },
        'address.postcode': function(newPostcode, oldPostcode) {
            newPostcode = (newPostcode || '').toLowerCase();
            oldPostcode = (oldPostcode || '').toLowerCase();

            if ((oldPostcode !== newPostcode) && this.$refs.postcode.isValid) {
                this.$emit('postcodeChanged');
                PostcodeService.fetchPostcodeDeliverableStatus(newPostcode)
                    .catch(() => this.toggleUndeliverablePostcodeModal(true));
            }
        },
    },
    async created() {
        if (this.couponCode) {
            this.fetchCouponCodeFromApi();
        }
    },
    methods: {
        async submitStep() {
            this.errorHandler.reset();
            this.isFormProcessing = true;
            try {
                const token = this.getCookie('access_token');
                const response = await axios.post(`${process.env.VUE_APP_API_URL}get-user-subscription-details`, {
                    email: this.email,
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.data.is_cancelled) {
                    this.isFormProcessing = false;
                    this.errorHandler.setResponse({ errors: { isCancelled: ' User has already an active subscription' } });
                    return;
                }
                this.subscriptionDetails = response.data;
                this.productFamilyOptions = this.subscriptionDetails.product_families;
                this.planFrequencyOptions = this.subscriptionDetails.plan_frequencies;
                this.editedProductFamily = this.subscriptionDetails.product_family;
                this.editedPlanFrequency = this.subscriptionDetails.plan_frequency;
                this.subscriptionId = this.subscriptionDetails.subscription_id;
                this.$store.dispatch('subscription/fetchAvailableDeliveryDay', this.subscriptionDetails.postcode);
            } catch (error) {
                if (error.response) {
                    if (error.response.data.message) {
                        this.errorHandler.setResponse({ errors: { message: error.response.data.message } });
                        return;
                    }
                    this.errorHandler.setResponse(error.response.data.message);
                } else {
                    this.errorHandler.setResponse('Network error, please try again');
                }
            } finally {
                this.isFormProcessing = false;
            }
        },
        getCookie(name) {
            const cookies = document.cookie;
            const cookieArray = cookies.split('; ');
            for (const cookie of cookieArray) {
                const [cookieName, cookieValue] = cookie.split('=');
                if (cookieName === name) {
                    return cookieValue;
                }
            }
            return null;
        },
        submitSubscription() {
            if (!this.showDelivery) {
                if (!this.address.postcode) {
                    this.errorHandler.setResponse({ errors: { postcode: 'Please enter postcode' } });
                    return;
                } else if (!this.address.addressLine1) {
                    this.errorHandler.setResponse({ errors: { address1: 'Please enter first line of address' } });
                    return;
                } else if (!this.address.city) {
                    this.errorHandler.setResponse({ errors: { city: 'Please enter city' } });
                    return;
                }
            }

            this.isSubFormProcessing = true;
            this.buttonDisabled = true;
            const requiredProductFamily = this.productFamilyOptions.find(item => item.id === this.editedProductFamily);
            let selectedProductFromDelivery = this.getProductTypes?.find(item => item.product_family === requiredProductFamily.name);
            let service = new SubscriptionService();
            service.createSubscriptionFromLapsedSubscription({
                subscription_id: this.subscriptionId,
                plan_slug: this.editedPlanFrequency,
                primary_product_id: selectedProductFromDelivery?.id,
                coupon_code: this.couponCode,
                source_id: this.sourceId,
                canvasser_id: this.canvasserId,
                region_id: this.regionId,
                dislike_lily: this.dislikeLily,
                likes_email: this.likesEmail,
                address: {
                    address1: this.address.addressLine1,
                    address2: this.address.addressLine2,
                    city: this.address.city,
                    postcode: this.address.postcode,
                },
                delivery_start_date: this.selectedDeliveryDate ? this.selectedDeliveryDate.value : null,
            })
                .then(() => {
                    this.isSubFormProcessing = false;
                    this.$router.push({ name: 'canvasser-register.restart-account-confirmation-page' });
                })
                .catch(error => {
                    this.isSubFormProcessing = false;
                    setTimeout(() => this.errorHandler.setResponse(error.response && error.response.data), 0);
                    this.buttonDisabled = false;
                });
        },
        fetchCouponCodeFromApi() {
            localStorage.removeItem('urlCouponCode');
            CouponService.getCouponByCouponCode(this.couponCode)
                .then(response => {
                    const couponProperties = response.data?.properties;
                    const couponHandle = couponProperties?.coupon_handle;
                    const couponCode = couponProperties?.code;
                    if (couponHandle) localStorage.setItem('urlCouponCode', couponHandle);
                    else localStorage.setItem('urlCouponCode', this.couponCode);
                    this.couponCode = formatCouponCode(couponCode);
                    this.$store.commit('register/setCouponProperties', couponProperties);
                    if (couponProperties.product_type !== GIFT_COUPON_TYPE) this.apiCoupon = { ...couponProperties, coupon_handle: response?.data?.properties?.coupon_handle };
                })
                .catch(error => console.error(error));
        },
        addressChanged() {
            this.$emit('addressChanged', this.address);
        },
        updateSelectedAddress(address) {
            this.address.addressLine1 = address.addressline1;
            this.address.addressLine2 = address.addressline2;
            this.address.city = address.county;
            this.showFullAddress = true;
            this.fetchDeliveryDateFromApi(this.address.postcode);
        },
        fetchDeliveryDateFromApi(postcode) {
            if (isPostcodeFormatValid(postcode)) {
                PostcodeService.GetFirstDeliveryDayForPostcode(postcode)
                    .then(response => {
                        const data = response.data.properties;
                        this.firstPossibleDeliveryDate = data.first_delivery_day;
                        this.courierName = data.courier_name;
                        this.preferredDeliveryDay = this.deliveryDay;
                    })
                    .catch(error => {
                        const response = error.response.data;

                        if (response.errors?.postcode) {
                            this.postcodeError = response.errors.postcode[0];
                        }
                    });
            }
        },
        clearAddress() {
            this.address = { addressLine1: null, addressLine2: null, city: null, postcode: null };
        },
        toggleUndeliverablePostcodeModal() {
            this.showUndeliverablePostcodeModal = !this.showUndeliverablePostcodeModal;
        },
        toggleDeliveryFields() {
            this.showDelivery = !this.showDelivery;
        },
    },
};
</script>
<style lang="scss" scoped>
.email {
width: 100%;
max-width: 100%;
padding: 10px;
font-size: 16px;
margin: 0 auto;
display: block;
}
.subscription-details {
padding: 20px;
background-color: #f9f9f9;
border-radius: 8px;
max-width: 600px;
margin: 0 auto;
}
.subscription-details h3 {
text-align: center;
margin-bottom: 20px;
font-size: 24px;
}
.subscription-details p {
font-size: 16px;
margin-bottom: 15px;
}
.subscription-details select {
width: 100%;
padding: 8px;
font-size: 16px;
border-radius: 4px;
border: 1px solid #ccc;
}
@media (max-width: 768px) {
.email {
    max-width: 500px;
    font-size: 18px;
}
.subscription-details h3 {
    font-size: 22px;
}
.subscription-details p {
    font-size: 14px;
}
.subscription-details select {
    font-size: 14px;
    padding: 6px;
}
}
@media (max-width: 480px) {
.subscription-details {
    padding: 15px;
}
.subscription-details h3 {
    font-size: 20px;
}
.subscription-details p {
    font-size: 13px;
}
.subscription-details select {
    font-size: 13px;
    padding: 5px;
}
}
@media (max-width: 320px) {
.subscription-details h3 {
    font-size: 18px;
}
.subscription-details p {
    font-size: 12px;
}
.subscription-details select {
    font-size: 12px;
    padding: 4px;
}
}
@media (min-width: 1024px) {
.email {
    max-width: 600px;
    font-size: 18px;
}
}
.error-message {
color: red;
font-weight: bold;
font-size: 14px;
margin-top: 10px;
display: block;
text-align: center;
}
.lily-input {
    margin-left: 10px;
    accent-color: black;
  }
  .likes-email {
    margin-left: 10px;
    accent-color: black;
  }
  .checkbox-container {
    display: flex;
    align-items: center;
  }

  .checkbox-container input[type="checkbox"] {
    margin-right: 10px;
  }

  .checkbox-container img {
    margin-right: 10px;
    width: 20px;
    height: 20px;
  }
  .button-container {
    display: flex;
    justify-content: flex-end;
}
.styled-button {
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    cursor: pointer;
    font-size: 16px;
    transition: background-color 0.3s;
}

.styled-button:hover {
    background-color: #0056b3;
}

.styled-button:focus {
    outline: none;
}
</style>
